<!--
  告警配置 · 监控州配置：州二字码 + 是否纳入监控（1 启用 / 0 停用）
  · 列表：监控州列表接口
  · 行内滑块：单州启用 / 禁用（即时生效）
  · 勾选后批量启用 / 禁用：批量更新接口
-->
<template>
  <div class="state-pane">
    <div class="card">
      <div class="card-header">
        <h3 style="margin-top: 0;">
          <span>监控州范围</span>
          <span class="ch-pt">Escopo de Estados Monitorados</span>
        </h3>
        <div class="hd-actions">
          <el-button
            size="small" :disabled="!selection.length" :loading="updating"
            @click="confirmBatch(1)"
          >
            <bi-label zh="批量启用" pt="Ativar em Lote" inline />
          </el-button>
          <el-button
            size="small" :disabled="!selection.length" :loading="updating"
            @click="confirmBatch(0)"
          >
            <bi-label zh="批量停用" pt="Desativar em Lote" inline />
          </el-button>
        </div>
      </div>

      <div class="card-tip">
        已选 <b>{{ selection.length }}</b> 个州，共 <b>{{ rows.length }}</b> 个州；停用后该州不再参与定时检查与告警推送。
        <span class="tip-pt">
          / {{ selection.length }} estado(s) selecionado(s) de {{ rows.length }}; estados desativados não entram na verificação agendada nem nos alertas.
        </span>
      </div>

      <el-table
        ref="tableRef" v-loading="loading"
        :data="rows" size="mini" border
        style="width: 100%"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="46" align="center" />

        <el-table-column prop="uf" min-width="140">
          <template slot="header">
            <bi-label zh="州（UF）" pt="Estado (UF)" />
          </template>
          <template slot-scope="{ row }">
            <span class="uf-cell">{{ row.uf }}</span>
          </template>
        </el-table-column>

        <el-table-column min-width="180">
          <template slot="header">
            <bi-label zh="状态" pt="Status" />
          </template>
          <template slot-scope="{ row }">
            <span :class="['st-tag', row.status === 1 ? 'st-on' : 'st-off']">
              {{ row.status === 1 ? '启用' : '停用' }}
              <span class="st-pt">/ {{ row.status === 1 ? 'Ativado' : 'Desativado' }}</span>
            </span>
          </template>
        </el-table-column>

        <el-table-column width="110" align="center">
          <template slot="header">
            <bi-label zh="启用监控" pt="Ativar" />
          </template>
          <template slot-scope="{ row }">
            <toggle-switch
              :value="row.status === 1"
              on-text="点击停用 / Clique para desativar"
              off-text="点击启用 / Clique para ativar"
              @click="handleToggle(row)"
            />
          </template>
        </el-table-column>

        <div slot="empty" class="tbl-empty">
          接口暂无监控州数据
          <span class="empty-pt">/ Nenhum estado retornado pela API</span>
        </div>
      </el-table>
    </div>

    <!-- 批量启用 / 停用二次确认 -->
    <confirm-dialog
      :visible.sync="confirmVisible"
      :title="confirmOpts.title"
      :title-pt="confirmOpts.titlePt"
      :subtitle="confirmOpts.subtitle"
      :desc="confirmOpts.desc"
      :desc-pt="confirmOpts.descPt"
      :ok-text="confirmOpts.okText"
      :ok-text-pt="confirmOpts.okTextPt"
      :kind="confirmOpts.kind"
      @confirm="confirmOpts.onOk"
    />
  </div>
</template>

<script>
import BiLabel from './BiLabel';
import ToggleSwitch from './ToggleSwitch';
import ConfirmDialog from './ConfirmDialog';
import {
  getInvoiceSubjectStatusMonitorStateList,
  getInvoiceSubjectStatusMonitorStateUpdate
} from '@/api/invoiceCenter';

export default {
  name: 'MonitorStatePane',
  components: { BiLabel, ToggleSwitch, ConfirmDialog },
  data() {
    return {
      // 监控州列表：[{ uf, status }]，status 1 启用 / 0 停用
      rows: [],
      selection: [],
      loading: false,
      updating: false,
      confirmVisible: false,
      confirmOpts: {
        title: '', titlePt: '', subtitle: '', desc: '', descPt: '',
        okText: '确认', okTextPt: 'Confirmar', kind: 'ok', onOk: () => {}
      }
    };
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    // ============ 接口 ============
    /** 拉取监控州列表：失败只提示，列表保持空态 */
    async fetchList() {
      this.loading = true;
      try {
        const res = await getInvoiceSubjectStatusMonitorStateList();
        this.rows = Array.isArray(res && res.data) ? res.data : [];
      } catch (e) {
        this.rows = [];
        this.$message.error('监控州列表接口请求失败，请稍后重试 / Falha ao consultar UFs');
        console.error('[监控州配置] 列表接口请求失败', e);
      } finally {
        this.loading = false;
      }
    },
    /**
     * 批量更新州状态
     * @param {Array<{uf: string, status: number}>} items 需要变更的州（1 启用 / 0 停用）
     */
    async updateStates(items) {
      this.updating = true;
      try {
        await getInvoiceSubjectStatusMonitorStateUpdate(items);
        await this.fetchList();
        return true;
      } catch (e) {
        this.$message.error('更新失败，请稍后重试 / Falha ao atualizar');
        console.error('[监控州配置] 更新接口请求失败', e);
        return false;
      } finally {
        this.updating = false;
      }
    },

    // ============ 交互 ============
    /** 勾选变化 */
    handleSelectionChange(val) {
      this.selection = val;
    },
    /** 行内滑块：单州启用 / 禁用，即时调接口 */
    async handleToggle(row) {
      const next = row.status === 1 ? 0 : 1;
      this.confirmBatch(next, [{ uf: row.uf, status: next }]);
      // const ok = await this.updateStates([{ uf: row.uf, status: next }]);
      // if (ok) {
      //   this.$message.success(`${row.uf} 已${next === 1 ? '启用' : '停用'}监控 / ${row.uf} ${next === 1 ? 'ativado' : 'desativado'}`);
      // }
    },
    /** 批量启用 / 停用：二次确认后调批量接口 */
    confirmBatch(status, items) {
      const ufs = items && items.length > 0 ? items.map(r => r.uf) : this.selection.map(r => r.uf);
      if (!ufs.length) return;
      const willEnable = status === 1;
      this.confirmOpts = {
        title: willEnable ? '批量启用监控' : '批量停用监控',
        titlePt: willEnable ? 'Ativar em Lote' : 'Desativar em Lote',
        subtitle: ufs.join('、'),
        desc: willEnable
          ? `确认后 <b>${ufs.length}</b> 个州将纳入定时检查与告警推送。`
          : `确认后 <b>${ufs.length}</b> 个州<b>不再参与</b>定时检查与告警推送。`,
        descPt: willEnable
          ? `Após a confirmação, <b>${ufs.length}</b> estado(s) entrarão na verificação agendada e nos alertas.`
          : `Após a confirmação, <b>${ufs.length}</b> estado(s) <b>não participarão</b> mais da verificação agendada nem dos alertas.`,
        okText: willEnable ? '确认启用' : '确认停用',
        okTextPt: willEnable ? 'Confirmar Ativação' : 'Confirmar Desativação',
        kind: willEnable ? 'ok' : 'warn',
        onOk: async() => {
          const ok = await this.updateStates(ufs.map(uf => ({ uf, status })));
          if (ok) {
            this.$message.success(`已${willEnable ? '启用' : '停用'} ${ufs.length} 个州 / ${ufs.length} estado(s) ${willEnable ? 'ativado(s)' : 'desativado(s)'}`);
          }
        }
      };
      this.confirmVisible = true;
    }
  }
};
</script>

<style scoped lang="scss">
.card {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 20px;
}
.card-header {
  border-bottom: 1px solid #f0f1f5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.card-header h3 {
  font-size: 15px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
}
.card-header h3 .ch-pt {
  font-size: 12px;
  font-weight: 400;
  color: #86909c;
  margin-top: 2px;
}
.hd-actions {
  display: flex;
  gap: 8px;
}
.card-tip {
  font-size: 12px;
  color: #86909c;
  padding: 10px 0;
  line-height: 1.6;
}
.card-tip .tip-pt {
  display: block;
  font-size: 12px;
}
.uf-cell {
  font-size: 13px;
  font-weight: 600;
  color: #1d2129;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
}
.st-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  line-height: 18px;
}
.st-tag .st-pt {
  font-size: 12px;
  font-weight: 400;
  margin-left: 2px;
}
.st-on { background: #e8f7e9; color: #00b42a; }
.st-off { background: #f2f3f5; color: #86909c; }
.tbl-empty {
  padding: 24px 0;
  font-size: 12px;
  color: #86909c;
}
.tbl-empty .empty-pt {
  display: block;
  font-size: 12px;
  margin-top: 2px;
}
</style>
